import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { leftoverDossiersToCardsPath } from '../utils/bookCards';
import { plottrackPath } from '../utils/plottrackPath';
import {
  PLOT_READY_NAME,
  PLOT_READY_OPEN_BOOK,
  PLOT_READY_OPEN_BOOK_TITLE,
  PLOT_READY_TIP,
  workshopIsReady,
} from '../utils/plotReadyCopy';
import { writerFacingError } from '../utils/plotTurns';
import { useApp } from '../context/AppContext';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import { plotDossiersApi } from '../api/plotDossiers';
import { uploadPathApi } from '../api/uploadPath';
import { UPLOAD_PATH_B_NEXT } from '../utils/writerCopy';
import UploadSettle from '../components/UploadGaps/UploadSettle';
import NarrativePovStrip from '../components/NarrativePov/NarrativePovSettle';
import { povNeedsSettle } from '../utils/narrativePov';
import { storiesApi } from '../api/stories';
import PlotDossier from '../components/Dossiers/PlotDossier';
import PlotReadyBoard from '../components/Dossiers/PlotReadyBoard';
import StoryPicker from '../components/Common/StoryPicker';
import NewSessionDialog from '../components/Session/NewSessionDialog';
import './DossiersPage.css';

function isCharacterLeftover(searchParams) {
  const kind = searchParams.get('kind');
  const name = searchParams.get('name');
  return kind === 'characters' || Boolean(name);
}

function cardKindForSlot(slot) {
  if (slot?.id === 'whose_story') return 'characters';
  if (slot?.id === 'world_rules') return 'place';
  return 'plot';
}

function focusTemplateForSlot(slot) {
  if (slot?.where === 'character_focus' || slot?.id === 'whose_story') return 'character';
  if (slot?.where === 'world_focus' || slot?.id === 'world_rules') return 'world';
  return 'plot';
}

export function plotReadyBuildError(err) {
  const facing = writerFacingError(err?.message || '');
  if (facing && facing !== 'Could not bring a turn here.') return facing;
  return 'Could not open that sequence.';
}

/**
 * Plot Workshop room (#1080 / #995). Outline readiness for Plot Grid.
 * Same /dossiers door. Character leftover URLs still land on Book Notes.
 * Plot notes (turns, Logline) sit on this page. There is no Long Plot write.
 */
export default function DossiersPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentStory, currentProject } = useApp();
  const openedId = currentStory?.id || null;
  const { storyId } = useSyncStoryContext({ routeStoryId: openedId });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [workshop, setWorkshop] = useState(null);
  const [beatError, setBeatError] = useState(null);
  const [focusOpen, setFocusOpen] = useState(false);
  const [focusTemplate, setFocusTemplate] = useState('plot');
  const [pathView, setPathView] = useState(null);
  const [bookPov, setBookPov] = useState(null);
  const projectId = currentProject?.id || currentStory?.project_id || null;

  const loadWorkshop = useCallback(async () => {
    if (!storyId) return;
    try {
      const next = await plotDossiersApi.plotWorkshop(storyId);
      setWorkshop(next);
    } catch (err) {
      console.error('Failed to load Plot Workshop:', err);
    }
  }, [storyId]);

  useEffect(() => {
    loadWorkshop();
  }, [loadWorkshop]);

  useEffect(() => {
    if (!storyId) {
      setPathView(null);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(uploadPathApi.view(storyId))
      .then((data) => { if (!cancelled) setPathView(data || null); })
      .catch(() => { if (!cancelled) setPathView(null); });
    return () => { cancelled = true; };
  }, [storyId]);

  const openDiscovery = useCallback(async (opener) => {
    const go = (sessionId) => {
      if (opener) {
        navigate(`/chat/${sessionId}`, { state: { pathGapOpener: opener } });
        return;
      }
      navigate(`/chat/${sessionId}`);
    };
    const sessionId = currentStory?.discovery_session_id;
    if (sessionId) {
      go(sessionId);
      return;
    }
    if (!storyId) return;
    try {
      const ensured = await storiesApi.ensureDiscoverySession(storyId);
      const nextId = ensured?.session_id || ensured?.id;
      if (nextId) go(nextId);
    } catch (err) {
      console.error('Failed to open Discovery:', err);
    }
  }, [currentStory, storyId, navigate]);

  const handleWhere = useCallback((slot) => {
    if (!slot?.where) return;
    if (slot.where === 'discovery') {
      openDiscovery(slot.starter_prompt || '');
      return;
    }
    if (slot.where === 'make_card') {
      navigate(`/bibles?kind=${cardKindForSlot(slot)}`);
      return;
    }
    if (slot.where === 'framework') {
      navigate('/frameworks');
      return;
    }
    if (
      slot.where === 'character_focus'
      || slot.where === 'plot_focus'
      || slot.where === 'world_focus'
    ) {
      setFocusTemplate(focusTemplateForSlot(slot));
      setFocusOpen(true);
    }
  }, [navigate, openDiscovery]);

  const handleSaveBeatFill = useCallback(async (beat) => {
    if (!storyId || !beat?.name) return;
    const saved = await plotDossiersApi.acceptWorkshopBeat(storyId, {
      beatName: beat.name,
      content: beat.content,
      replace: true,
    });
    const savedName = saved?.beat_name || beat.name;
    const savedContent = saved?.content ?? beat.content;
    setWorkshop((current) => {
      if (!current?.beats) return current;
      return {
        ...current,
        beats: current.beats.map((row) => (
          row.name === savedName ? { ...row, content: savedContent } : row
        )),
      };
    });
  }, [storyId]);

  const handleUseFrameworkAsPlan = useCallback(async () => {
    if (!storyId) return;
    try {
      await uploadPathApi.setPlanMode(storyId, 'framework_as_plan');
      await loadWorkshop();
    } catch (err) {
      setBeatError(plotReadyBuildError(err));
    }
  }, [storyId, loadWorkshop]);

  const handleWorkBeat = useCallback(async (beat) => {
    // One-click Plot Focus for this Framework beat (#1096).
    // Per-beat session: template=plot, name Work {beat}. Not Scene Focus.
    if (!storyId || !beat?.name) return;
    try {
      const opened = await plotDossiersApi.openWorkshopBeat(storyId, beat.name);
      if (opened?.session_id) navigate(`/chat/${opened.session_id}`);
    } catch (err) {
      console.error('Failed to open Work this beat:', err);
      setBeatError(plotReadyBuildError(err));
    }
  }, [storyId, navigate]);

  if (isCharacterLeftover(searchParams)) {
    return <Navigate to={leftoverDossiersToCardsPath(searchParams)} replace />;
  }

  if (!storyId) {
    return (
      <div className="plot-notebook-page">
        <p className="plot-notebook-page__kicker">The Book</p>
        <h1>{PLOT_READY_NAME}</h1>
        <p className="plot-notebook-page__tip">{PLOT_READY_TIP}</p>
        <p>{PLOT_READY_OPEN_BOOK}</p>
        <button type="button" className="fw-btn fw-btn--primary" onClick={() => setPickerOpen(true)}>
          Open a book
        </button>
        <StoryPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title={PLOT_READY_OPEN_BOOK_TITLE}
        />
      </div>
    );
  }

  return (
    <div className="plot-notebook-page">
      <p className="plot-notebook-page__kicker">The Book</p>
      <h1>{PLOT_READY_NAME}</h1>
      <p className="plot-notebook-page__tip">{PLOT_READY_TIP}</p>
      {searchParams.get('from') === 'upload-b' ? (
        <p className="plot-notebook-page__tip" role="status">{UPLOAD_PATH_B_NEXT}</p>
      ) : null}
      <NarrativePovStrip storyId={storyId} onChange={setBookPov} />
      <UploadSettle
        gaps={pathView?.gaps}
        room="focus"
        povSlot={povNeedsSettle(bookPov)}
        onOpenNotes={projectId ? () => navigate(`/projects/${projectId}/stories/${storyId}/notes`) : undefined}
      />
      <PlotReadyBoard
        workshop={workshop}
        onWhere={handleWhere}
        onWorkBeat={handleWorkBeat}
        onSaveFill={handleSaveBeatFill}
        onUseFrameworkAsPlan={handleUseFrameworkAsPlan}
        onOpenPlottrack={() => {
          const ready = workshopIsReady(workshop);
          const hasOutline = Boolean(workshop?.has_outline);
          if (!ready && !hasOutline) return;
          navigate(plottrackPath(storyId), {
            state: { startGenerate: ready && !hasOutline },
          });
        }}
        onChooseFramework={() => navigate('/frameworks')}
        hasOutline={Boolean(workshop?.has_outline)}
      />
      {beatError ? (
        <p className="plot-notebook-page__error" role="alert">{beatError}</p>
      ) : null}
      <PlotDossier storyId={storyId} />
      {focusOpen && (
        <NewSessionDialog
          storyId={storyId}
          initialPrimaryMode="focus"
          initialFocusTemplate={focusTemplate}
          onClose={() => setFocusOpen(false)}
          onSessionCreated={(session) => {
            setFocusOpen(false);
            if (session?.id) navigate(`/chat/${session.id}`);
          }}
        />
      )}
    </div>
  );
}
